import { Atom, atom } from 'jotai';
import { PromiseAtom } from '@/types/atom';
import { SuspenseAtomResult } from '@/types/jotai';
import { MetricDefinition, PHYSICAL_SOURCE } from '@/types/metric';
import { RadarData, RadarTemplate } from '@/types/radar';
import { DataTableColumn } from '@/types/table';
import { AGGREGATES_COLUMN_PREFIX, decimalPlaces, decimalPlacesFromFormatRule } from '@/utils/metrics';
import { snakeToCamel } from '@/utils/queries';
import { formatRadarValue } from '@/utils/radar';
import { radarTemplateIdAtom } from '../radar';

const isRadarData = (item: RadarData | undefined): item is RadarData => !!item;

type RadarDataAtom = SuspenseAtomResult<Record<string, number>>;

export const radarDataAtom = (
  dataAtom: Atom<RadarDataAtom | Promise<RadarDataAtom>>,
  metricDataAtom: PromiseAtom<MetricDefinition[]>,
  radarTemplatesAtom: PromiseAtom<RadarTemplate[]>,
  columnsAtom: PromiseAtom<DataTableColumn[]>,
) =>
  atom(async get => {
    const rawRadarData = (await get(dataAtom)).data;

    const metricData = await get(metricDataAtom);
    const selectedRadarTemplateId = await get(radarTemplateIdAtom);
    const radarTemplates = await get(radarTemplatesAtom);

    const selectedRadarTemplate = radarTemplates.find(({ id }) => id === selectedRadarTemplateId);
    if (!selectedRadarTemplate) throw new Error('Radar template not found');

    const hasPhysicalMetric = selectedRadarTemplate.metrics.some(
      ({ key }) => metricData.find(candidate => candidate.key === key)?.source === PHYSICAL_SOURCE,
    );
    const tableColumns = hasPhysicalMetric ? await get(columnsAtom) : [];
    const tableColumnsByKey = new Map(tableColumns.map(column => [column.tableColumn.key, column]));

    return selectedRadarTemplate.metrics
      .map(({ key, min, max }) => {
        const metric = metricData.find(candidate => key === candidate.key);
        if (!metric) return undefined;

        const formattedMin = formatRadarValue(min, metric.percentage);
        const formattedMax = formatRadarValue(max, metric.percentage);

        const minValue = metric.reverseScale ? formattedMax : formattedMin;
        const maxValue = metric.reverseScale ? formattedMin : formattedMax;

        const rawValue = rawRadarData[metric.keyNormalised];
        // treat only a finite number as real data — null, undefined and NaN all fall through to n/a
        const hasValue = Number.isFinite(rawValue);

        const columnKey = `${AGGREGATES_COLUMN_PREFIX}${snakeToCamel(metric.keyNormalised)}`;
        const matchingColumn = metric.source === PHYSICAL_SOURCE ? tableColumnsByKey.get(columnKey) : undefined;
        const metricDecimalPlaces = matchingColumn
          ? decimalPlacesFromFormatRule(matchingColumn.tableColumn.formatRule)
          : decimalPlaces(minValue, maxValue);

        return {
          ...metric,
          minValue,
          maxValue,
          decimalPlaces: metricDecimalPlaces,
          value: formatRadarValue(hasValue ? rawValue : minValue, hasValue ? metric.percentage : false),
          hasValue,
        };
      })
      .filter(isRadarData);
  });
